import { useEffect } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Box from '@material-ui/core/Box';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Workspace IDs field for the bulk deploy template.
 *
 * Reads the `environments` array from sibling form fields via formContext.
 * When multiple environments are selected the field is disabled and a warning
 * is shown — workspace IDs are environment-specific so they cannot be applied
 * across environments in a single bulk run.
 *
 * Uses: `ui:field: BulkWorkspaceIds` in bulk-template.yaml.
 */
export const BulkWorkspaceIdsField = ({
  onChange,
  rawErrors,
  formContext,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const formValues = formContext?.formData as Record<string, any> | undefined;
  const environments: string[] = formValues?.environments ?? [];
  const currentValue = (formValues?.workspaceIds || '') as string;
  const isMultiEnv = environments.length > 1;

  // Clear the value when switching to multi-env so stale IDs aren't submitted
  useEffect(() => {
    if (isMultiEnv && currentValue) {
      onChange('');
    }
  }, [isMultiEnv, currentValue, onChange]);

  const errorMessage = rawErrors?.[0];

  return (
    <FormControl
      margin="normal"
      error={rawErrors?.length > 0 && !rawErrors[0]?.startsWith('__no__error__')}
      disabled={isMultiEnv}
      fullWidth
    >
      <InputLabel htmlFor="workspaceIds" shrink>
        {schema.title || 'Workspace IDs'}
      </InputLabel>
      <Input
        id="workspaceIds"
        aria-describedby="workspaceIds-helper-text"
        value={isMultiEnv ? '' : currentValue}
        onChange={e => onChange(e.target?.value)}
        disabled={isMultiEnv}
        placeholder={
          isMultiEnv
            ? 'Disabled — select a single environment to set workspace IDs'
            : 'Enter comma-separated workspace IDs (e.g. 1-1111111, 1-2222222)'
        }
      />
      {isMultiEnv && (
        <Box
          mt={1}
          p={1.5}
          bgcolor="#fff3e0"
          border="1px solid #ff9800"
          borderRadius={4}
        >
          <FormHelperText
            style={{ color: '#e65100', fontSize: '0.85rem', margin: 0 }}
          >
            <strong>NOTE:</strong> Workspace IDs cannot be set when deploying
            to multiple environments. Workspace restrictions are
            environment-specific. To set workspace IDs, deploy to one
            environment at a time.
          </FormHelperText>
        </Box>
      )}
      {!isMultiEnv && (
        <FormHelperText id="workspaceIds-helper-text">
          Comma-separated Account External IDs (found in Django Site Admin &rarr;
          Account &rarr; "External ID"). 
          If specified, this overwrites any existing workspace restrictions for the deployed agents. If left empty, any existing workspace restriction will be left as-is. 
        </FormHelperText>
      )}
      {errorMessage && (
        <FormHelperText error>{errorMessage}</FormHelperText>
      )}
    </FormControl>
  );
};
